import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";

const MAX_LOG_STRING = 12000;

/**
 * Strip FieldValues / Timestamps into plain JSON-safe data for Firestore logs.
 */
export function sanitizeForLog(value, depth = 0) {
  if (value == null) return value;
  if (depth > 8) return "[max-depth]";

  if (typeof value === "string") {
    return value.length > MAX_LOG_STRING
      ? `${value.slice(0, MAX_LOG_STRING)}…[truncated]`
      : value;
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  // Firestore Timestamp
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate().toISOString();
    } catch {
      return null;
    }
  }
  if (
    typeof value === "object" &&
    typeof value.seconds === "number" &&
    typeof value.nanoseconds === "number"
  ) {
    return new Date(value.seconds * 1000).toISOString();
  }

  // FieldValue / sentinel (e.g. serverTimestamp)
  if (typeof value === "object" && value._methodName) {
    return `[FieldValue:${value._methodName}]`;
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForLog(item, depth + 1));
  }

  if (typeof value === "object") {
    const out = {};
    for (const [key, nested] of Object.entries(value)) {
      // Avoid duplicating audit stamps inside before/after payloads.
      if (key.startsWith("last_edit_")) continue;
      out[key] = sanitizeForLog(nested, depth + 1);
    }
    return out;
  }

  return String(value);
}

/**
 * Resolve the editor's public IP and approximate location.
 * Never throws — falls back to "unknown" so saves are not blocked.
 */
export async function getClientEditMeta({
  section,
  action,
  targetId = null,
} = {}) {
  let ip = "unknown";
  let location = "Unknown";
  let city = null;
  let region = null;
  let country = null;

  try {
    const res = await fetch("https://ipapi.co/json/", { cache: "no-store" });
    if (res.ok) {
      const data = await res.json();
      if (data?.ip) ip = String(data.ip);
      city = data.city || null;
      region = data.region || null;
      country = data.country_name || null;
      const parts = [city, region, country].filter(Boolean);
      if (parts.length) location = parts.join(", ");
    } else {
      throw new Error("ipapi failed");
    }
  } catch {
    try {
      const ipRes = await fetch("https://api.ipify.org?format=json", {
        cache: "no-store",
      });
      if (ipRes.ok) {
        const data = await ipRes.json();
        if (data?.ip) ip = String(data.ip);
      }
    } catch {
      // keep defaults
    }
  }

  const by = auth.currentUser?.email || auth.currentUser?.uid || "unknown";

  return {
    last_edit_ip: ip,
    last_edit_location: location,
    last_edit_at: serverTimestamp(),
    last_edit_by: by,
    last_edit_section: section || null,
    last_edit_action: action || null,
    last_edit_target_id: targetId,
    _geo: { city, region, country },
  };
}

/**
 * Full immutable audit row in `admin_edit_logs` (view via Firebase Console only).
 * Includes before/after snapshots, actor, IP, and location.
 */
export async function writeFullAuditLog({
  section,
  action,
  collectionName,
  targetId = null,
  before = null,
  after = null,
  meta = null,
  extra = null,
} = {}) {
  try {
    const resolved =
      meta ||
      (await getClientEditMeta({
        section,
        action,
        targetId,
      }));

    const entry = {
      source: "targe-tube-admin",
      page_url:
        typeof window !== "undefined" ? window.location.href : null,
      section: section || resolved.last_edit_section || null,
      action: action || resolved.last_edit_action || null,
      collection: collectionName || null,
      target_id: targetId || resolved.last_edit_target_id || null,
      target_path:
        collectionName && (targetId || resolved.last_edit_target_id)
          ? `${collectionName}/${targetId || resolved.last_edit_target_id}`
          : collectionName || null,
      ip: resolved.last_edit_ip || "unknown",
      location: resolved.last_edit_location || "Unknown",
      city: resolved._geo?.city || null,
      region: resolved._geo?.region || null,
      country: resolved._geo?.country || null,
      by: resolved.last_edit_by || "unknown",
      by_uid: auth.currentUser?.uid || null,
      user_agent:
        typeof navigator !== "undefined" ? navigator.userAgent : null,
      before: sanitizeForLog(before),
      after: sanitizeForLog(after),
      changed_fields: diffFieldNames(
        sanitizeForLog(before),
        sanitizeForLog(after)
      ),
      at: serverTimestamp(),
    };

    if (extra && typeof extra === "object") {
      entry.extra = sanitizeForLog(extra);
    }

    await addDoc(collection(db, "admin_edit_logs"), entry);
  } catch (e) {
    console.warn("Failed to write admin_edit_logs entry:", e);
  }
}

/** @deprecated use writeFullAuditLog */
export async function writeEditAuditLog(meta, options = {}) {
  await writeFullAuditLog({
    section: meta?.last_edit_section,
    action: meta?.last_edit_action,
    collectionName: options.collectionName || null,
    targetId: meta?.last_edit_target_id,
    before: options.before ?? null,
    after: options.after ?? null,
    meta,
    extra: options.extra ?? null,
  });
}

export function formatEditAt(value) {
  if (!value) return "—";
  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleString();
    }
    if (value?.seconds) {
      return new Date(value.seconds * 1000).toLocaleString();
    }
    return new Date(value).toLocaleString();
  } catch {
    return "—";
  }
}     

/** List top-level keys that differ between two plain objects. */
export function diffFieldNames(before, after) {
  if (!before || !after) return null;
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  const changed = [];
  for (const key of keys) {
    try {
      if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
        changed.push(key);
      }
    } catch {
      changed.push(key);
    }
  }
  return changed;
}

/** Business fields only — for before/after slider snapshots. */
export function sliderSnapshot(slider) {
  if (!slider) return null;
  return {
    id: slider.id || null,
    image: slider.image || "",
    title: slider.title || "",
    link_type: slider.link_type || "",
    link: slider.link || "",
    order: slider.order ?? 0,
    is_active: slider.is_active !== false,
  };
}

/** Website content snapshot without audit stamps. */
export function websiteSnapshot(data) {
  if (!data) return null;
  const {
    last_edit_ip,
    last_edit_location,
    last_edit_at,
    last_edit_by,
    last_edit_section,
    last_edit_action,
    last_edit_target_id,
    ...rest
  } = data;
  return rest;
}
